import React, { useState, useEffect } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import './CheckoutForm.css'
import SiteNavBar from '../navigation/SiteNavBar';
import Footer from '../footer/Footer';
import CopyRight from '../footer/CopyRight';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { config } from '../../../config';

const InnerPaymentForm = ({
  clientSecret,
  onSuccess,
  paymentMethods = [],
  defaultPaymentMethodId = null,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  // "new" = use PaymentElement (enter new card)
  // otherwise = use saved payment_method id
  const [selectedMethodId, setSelectedMethodId] = useState(
    defaultPaymentMethodId || "new"
  );

  const handlePay = async () => {
    if (!stripe) return;

    setSubmitting(true);

    let result;

    if (selectedMethodId === "new") {
      // New card via PaymentElement
      if (!elements) {
        setSubmitting(false);
        return;
      }

      // Optional: validate PaymentElement fields
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setSubmitting(false);
        alert(submitError.message || "Invalid card details");
        return;
      }

      result = await stripe.confirmPayment({
        elements,
        // we already passed clientSecret to <Elements>, so not needed here
        confirmParams: {},
        redirect: "if_required",
      });
    } else {
      // Saved card – confirm using payment_method id
      result = await stripe.confirmPayment({
        clientSecret, // use the PI client secret
        confirmParams: {
          payment_method: selectedMethodId,
        },
        redirect: "if_required",
      });
    }

    setSubmitting(false);

    const { error, paymentIntent } = result;

    if (error) {
      alert(error.message || "Payment failed");
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      onSuccess();
    } else {
      alert("Payment not completed.");
    }
  };

  const selectedMethod =
    selectedMethodId !== "new"
      ? paymentMethods.find((pm) => pm.id === selectedMethodId)
      : null;

  return (
    <div>
      {/* 1. Card dropdown */}
      <div style={{ marginBottom: "12px" }}>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: 4 }}>
          Payment Method
        </label>
        <select
          className="form-control"
          value={selectedMethodId}
          onChange={(e) => setSelectedMethodId(e.target.value)}
        >
          {/* Option to use new card */}
          <option value="new">Use a new card</option>

          {/* Saved cards */}
          {paymentMethods.map((pm) => (
            <option key={pm.id} value={pm.id}>
              {pm.card.brand.toUpperCase()} •••• {pm.card.last4} (exp{" "}
              {String(pm.card.exp_month).padStart(2, "0")}/
              {pm.card.exp_year})
            </option>
          ))}
        </select>
      </div>

      {/* 2. Show either PaymentElement OR saved card summary */}
      {selectedMethodId === "new" ? (
        <div style={{ marginBottom: "12px" }}>
          <PaymentElement />
        </div>
      ) : (
        selectedMethod && (
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: 4,
              padding: "8px 10px",
              marginBottom: "12px",
              fontSize: "14px",
            }}
          >
            <div>
              <strong>Selected card:</strong>{" "}
              {selectedMethod.card.brand.toUpperCase()} ••••{" "}
              {selectedMethod.card.last4}
            </div>
            <div>
              <strong>Expires:</strong>{" "}
              {String(selectedMethod.card.exp_month).padStart(2, "0")}/
              {selectedMethod.card.exp_year}
            </div>
          </div>
        )
      )}

      {/* 3. Pay button */}
      <button
        type="button"
        className="boxed-btn"
        onClick={handlePay}
        disabled={submitting || !stripe}
      >
        {submitting ? "Processing…" : "Pay now"}
      </button>
    </div>
  );
};

const CheckoutForm = () => {
    const { cart, clearCart } = useCart();
    const { token } = useAuth();
    // Address state
    const [mainAddress, setMainAddress] = useState({ street: '', city: '', state: '', zip: '' });
    const [billingAddress, setBillingAddress] = useState({ street: '', city: '', state: '', zip: '' });
    const [sameAsMain, setSameAsMain] = useState(true);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
    const [customerId, setCustomerId] = useState(null)

    // Sync billing address if same as main
    useEffect(() => {
        if (sameAsMain) setBillingAddress(mainAddress);
    }, [sameAsMain, mainAddress]);

    const handleMainChange = e => setMainAddress({ ...mainAddress, [e.target.name]: e.target.value });
    const handleBillingChange = e => setBillingAddress({ ...billingAddress, [e.target.name]: e.target.value });

    const [clientSecret, setClientSecret] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);

    useEffect(() => {
        // Initialize Stripe for orders
        if (config.STRIPE_PLATFORM_PUBLISHABLE_KEY) {
            setStripePromise(loadStripe(config.STRIPE_PLATFORM_PUBLISHABLE_KEY));
        }
    }, []);

    useEffect(() => {
    // Example: fetch saved methods for logged-in user
    const fetchMethods = async () => {
      const res = await axios.get(`${config.API_BASE}/api/payment-methods`);
      const { methods, defaultPaymentMethodId, customerId } = res.data;
      setPaymentMethods(methods || []);
      setDefaultPaymentMethodId(defaultPaymentMethodId || null);
      setCustomerId(customerId);
    };
    fetchMethods();
  }, []);

    const createIntent = async () => {
        const items = cart.map(item => ({
            productId: item.productId || item._id || item.id,
            quantity: item.quantity
        }));
        const res = await axios.post(`${config.API_BASE}/api/payments/create-intent`, {
            items
        });
        setClientSecret(res.data.clientSecret);
    };

    const placeOrder = async () => {
        const orderPayload = {
            items: cart.map(item => ({
                productId: item.productId || item._id || item.id,
                producerId: item.producerId,
                periodId: item.periodId,
                quantity: item.quantity,
                price: item.price ?? item.cost
            })),
            shippingAddress: mainAddress,
            billingAddress: billingAddress
        };
        const response = await axios.post(`${config.API_BASE}/api/orders`, orderPayload, {
            headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Order placed successfully:', response.data);
        clearCart();
        window.location.href = '/order-confirmation';
    };

    return (
        <div>
            <SiteNavBar />
            <div className="hero-area hero-bg" style={{ paddingTop: '3%' }}>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-9 offset-lg-2 text-center">
                            <div className="hero-text">
                                <div className="hero-text-tablecell" style={{ padding: '15% 0' }}>
                                    <p className="subtitle" style={{ fontSize: '20px' }}>Farmers Marketspace</p>
                                    <h1>Check Out Product</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="checkout-section mt-80 mb-80">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 text-right">
                            <a href="/shop" className="boxed-btn mt-0 mb-3">Countinue Shopping</a>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-6">
                            <div className="checkout-accordion-wrap">
                                <div className="accordion" id="accordionExample">
                                    <div className="card single-accordion">
                                        <div className="card-header" id="headingOne">
                                            <h5 className="mb-0">
                                                <button className="btn btn-link" type="button" data-toggle="collapse" data-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                                                    Billing Address
                                                </button>
                                            </h5>
                                        </div>
                                        <div id="collapseOne" className="collapse show" aria-labelledby="headingOne" data-parent="#accordionExample">
                                            <div className="card-body">
                                                <div className="billing-address-form">
                                                    <form onSubmit={e => e.preventDefault()}>
                                                        <p><input type="text" name="street" placeholder="Street" value={mainAddress.street} onChange={handleMainChange} required /></p>
                                                        <p><input type="text" name="city" placeholder="City" value={mainAddress.city} onChange={handleMainChange} required /></p>
                                                        <p><input type="text" name="state" placeholder="State" value={mainAddress.state} onChange={handleMainChange} required /></p>
                                                        <p><input type="text" name="zip" placeholder="Zip" value={mainAddress.zip} onChange={handleMainChange} required /></p>
                                                        <p><label><input type="checkbox" checked={sameAsMain} onChange={e => setSameAsMain(e.target.checked)} /> Same as main address</label></p>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card single-accordion">
                                        <div className="card-header" id="headingTwo">
                                            <h5 className="mb-0">
                                                <button className="btn btn-link collapsed" type="button" data-toggle="collapse" data-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                                                    Shipping Address
                                                </button>
                                            </h5>
                                        </div>
                                        <div id="collapseTwo" className="collapse" aria-labelledby="headingTwo" data-parent="#accordionExample">
                                            <div className="card-body">
                                                <div className="shipping-address-form">
                                                    <form onSubmit={e => e.preventDefault()}>
                                                        <p><input type="text" name="street" placeholder="Street" value={billingAddress.street} onChange={handleBillingChange} required={!sameAsMain} /></p>
                                                        <p><input type="text" name="city" placeholder="City" value={billingAddress.city} onChange={handleBillingChange} required={!sameAsMain} /></p>
                                                        <p><input type="text" name="state" placeholder="State" value={billingAddress.state} onChange={handleBillingChange} required={!sameAsMain} /></p>
                                                        <p><input type="text" name="zip" placeholder="Zip" value={billingAddress.zip} onChange={handleBillingChange} required={!sameAsMain} /></p>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card single-accordion">
                                        <div className="card-header" id="headingThree">
                                            <h5 className="mb-0">
                                                <button className="btn btn-link collapsed" type="button" data-toggle="collapse" data-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree" onClick={createIntent}>
                                                    Card Details
                                                </button>
                                            </h5>
                                        </div>
                                        <div id="collapseThree" className="collapse" aria-labelledby="headingThree" data-parent="#accordionExample">
                                            <div className="card-body">
                                                {clientSecret && stripePromise ? (
                                                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                                                        <InnerPaymentForm clientSecret={clientSecret} onSuccess={placeOrder} />
                                                    </Elements>
                                                ) : (
                                                    <p>Click to load payment form.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="order-details-wrap">
                                <table className="order-details" style={{width: '100%'}}>
                                    <thead>
                                        <tr>
                                            <th>Your order Details</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody className="order-details-body">
                                        <tr>
                                            <td>Product</td>
                                            <td>Total</td>
                                        </tr>
                                        <tr>
                                            <td>Strawberry</td>
                                            <td>$85.00</td>
                                        </tr>
                                        <tr>
                                            <td>Berry</td>
                                            <td>$70.00</td>
                                        </tr>
                                        <tr>
                                            <td>Lemon</td>
                                            <td>$35.00</td>
                                        </tr>
                                    </tbody>
                                    <tbody className="checkout-details">
                                        <tr>
                                            <td>Subtotal</td>
                                            <td>$190.00</td>
                                        </tr>
                                        <tr>
                                            <td>Shipping</td>
                                            <td>$50.00</td>
                                        </tr>
                                        <tr>
                                            <td>Total</td>
                                            <td>$240.00</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <button type="button" className="boxed-btn" onClick={createIntent}>Proceed to Payment</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            <CopyRight />
        </div>
    )
}

export default CheckoutForm;